import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { ArrowLeft, Phone, EnvelopeSimple, CalendarBlank, Handshake, ListChecks } from "@phosphor-icons/react";

const Contact360 = ({ user, logout }) => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`${API}/crm/contacts/${contactId}/360`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [contactId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTypeIcon = (type) => {
    if (type === 'call') return <Phone size={16} weight="fill" className="text-[#10B981]" />;
    if (type === 'email') return <EnvelopeSimple size={16} weight="fill" className="text-[#002FA7]" />;
    if (type === 'meeting') return <CalendarBlank size={16} weight="fill" className="text-[#F59E0B]" />;
    return <EnvelopeSimple size={16} weight="fill" className="text-[#475569]" />;
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50"><Sidebar user={user} logout={logout} /><div className="flex-1 ml-64 flex justify-center items-center"><div className="loading-spinner"></div></div></div>;

  if (!data) return <div className="flex min-h-screen bg-slate-50"><Sidebar user={user} logout={logout} /><div className="flex-1 ml-64 p-8"><p>Contact not found.</p></div></div>;

  const c = data.contact;
  const s = data.stats;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <button data-testid="back-btn" onClick={() => navigate('/crm/contacts')} className="flex items-center gap-2 text-slate-600 hover:text-[#002FA7] mb-6">
          <ArrowLeft size={20} /> Back to Contacts
        </button>

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-none p-8 mb-6 shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <h1 data-testid="contact-360-name" className="text-4xl font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>{c.name}</h1>
              <p className="text-slate-600">{c.position}{c.position && c.company ? ' at ' : ''}{c.company}</p>
              <p className="text-sm text-slate-500 mt-1">{c.email} {c.phone && `| ${c.phone}`}</p>
              {c.tags?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {c.tags.map(tag => <span key={tag} className="rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">{tag}</span>)}
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Assigned to</p>
              <p className="text-sm font-medium mt-1">{c.assigned_to || 'Unassigned'}</p>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div data-testid="contact-360-stats" className="grid grid-cols-5 gap-4 mb-6">
          {[
            { label: "Deals", value: s.total_deals, color: "#002FA7", icon: Handshake },
            { label: "Deal Value", value: `₹${s.total_deal_value.toLocaleString('en-IN')}`, color: "#10B981", icon: Handshake },
            { label: "Won Value", value: `₹${s.won_deal_value.toLocaleString('en-IN')}`, color: "#F59E0B", icon: Handshake },
            { label: "Activities", value: s.total_activities, color: "#475569", icon: CalendarBlank },
            { label: "Pending Tasks", value: s.pending_tasks, color: "#DC2626", icon: ListChecks },
          ].map(item => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="bg-white border border-slate-200 rounded-none p-4 shadow-sm">
                <Icon size={20} weight="duotone" style={{color: item.color}} className="mb-2" />
                <div className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: item.color}}>{item.value}</div>
                <div className="text-xs text-slate-500">{item.label}</div>
              </div>
            );
          })}
        </div>

        {/* Deals + Activities + Tasks */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Deals ({data.deals.length})</h3>
            <div className="space-y-3">
              {data.deals.map(d => (
                <div key={d.deal_id} className="border-b border-slate-100 pb-3 last:border-0">
                  <p className="font-medium text-sm">{d.title}</p>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-slate-500 uppercase">{d.stage?.replace('_', ' ')}</span>
                    <span className="text-sm font-bold text-[#002FA7]">₹{d.value?.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              ))}
              {data.deals.length === 0 && <p className="text-sm text-slate-400">No deals</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Activities ({data.activities.length})</h3>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {data.activities.map(a => (
                <div key={a.activity_id} className="flex items-start gap-2 border-b border-slate-100 pb-3 last:border-0">
                  {getTypeIcon(a.type)}
                  <div>
                    <p className="text-sm">{a.description}</p>
                    <p className="text-xs text-slate-400 mt-1">{a.created_at && new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {data.activities.length === 0 && <p className="text-sm text-slate-400">No activities</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Tasks ({data.tasks.length})</h3>
            <div className="space-y-3">
              {data.tasks.map(t => (
                <div key={t.task_id} className="border-b border-slate-100 pb-3 last:border-0">
                  <p className="font-medium text-sm">{t.title}</p>
                  <div className="flex gap-2 mt-1">
                    <span className={`rounded-none px-2 py-0.5 text-xs font-bold uppercase border ${t.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>{t.status}</span>
                    <span className="text-xs text-slate-400">{t.due_date}</span>
                  </div>
                </div>
              ))}
              {data.tasks.length === 0 && <p className="text-sm text-slate-400">No tasks</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact360;
